import { alCambiarDeSesion } from "./estadoDeSesion";
import { getSession } from "./auth";

/**
 * Preferencias de accesibilidad: tamaño del texto, alto contraste, letra fácil
 * de leer, sin animaciones y enlaces subrayados. Más la lectura en voz alta.
 *
 * POR QUÉ PROPIO Y NO UN OVERLAY. El portal público de la AVP usa UserWay. Un
 * overlay de terceros inyecta un script ajeno en una plataforma de menores de
 * una agencia de gobierno, y además no arregla lo que está mal debajo: pinta
 * encima. Aquí cada opción cambia lo mismo que usa la plataforma —los tokens de
 * tokens.css y el `font-size` de <html>—, así que lo que se arregla en la base
 * se nota también con el panel cerrado.
 *
 * CÓMO SE APLICA. Todo va a <html>, que es lo único que existe antes de que
 * React pinte nada (main.tsx llama a `iniciarAccesibilidad()` antes del
 * `render`, para que no haya un parpadeo con la letra pequeña):
 *   - tamaño  → `style.fontSize` en %, que escala toda la escala `--t-*` (rem)
 *               y RESPETA el tamaño que la persona ya tenga en su navegador;
 *   - el resto → una clase cada una (`alto-contraste`, `dislexia`,
 *               `sin-movimiento`, `subrayar`), con sus reglas en
 *               estilos/accesibilidad.css y estilos/base.css.
 *
 * EL KIOSCO. En una computadora compartida lo que eligió un visitante no puede
 * quedarse para el siguiente: el niño que puso la letra al 150 % se va, y el
 * adulto que llega no sabe por qué todo se ve enorme ni dónde se quita. Por eso
 * se restablecen al cambiar de sesión (salir, entrar) y cuando el kiosco se
 * reinicia por inactividad (`KioscoIdle` llama al mismo `limpiarEstadoDeSesion`).
 * Ver `alCambiarDeSesion` más abajo para la excepción de la pantalla de entrada.
 */

export type Tamano = 100 | 125 | 150;

export interface Preferencias {
  tamano: Tamano;
  contraste: boolean;
  dislexia: boolean;
  sinMovimiento: boolean;
  subrayarEnlaces: boolean;
}

export const PREFERENCIAS_INICIALES: Readonly<Preferencias> = Object.freeze({
  tamano: 100,
  contraste: false,
  dislexia: false,
  sinMovimiento: false,
  subrayarEnlaces: false,
});

export const TAMANOS: readonly Tamano[] = [100, 125, 150];

const CLAVE = "ikids.accesibilidad";
/*
 * Marca de "estas preferencias se eligieron ANTES de entrar". Vive en
 * `sessionStorage` (muere con la pestaña), no en `localStorage`: solo tiene que
 * sobrevivir el rato entre la pantalla de entrada y el clic en "Entrar".
 */
const MARCA_ANTES_DE_ENTRAR = "ikids.accesibilidad.antesDeEntrar";

const CLASES = {
  contraste: "alto-contraste",
  dislexia: "dislexia",
  sinMovimiento: "sin-movimiento",
  subrayarEnlaces: "subrayar",
} as const;

/* ---------------- Lectura y guardado ---------------- */

/**
 * Lee lo guardado, validando campo a campo. Lo que hay en `localStorage` lo
 * pudo escribir otra versión de la app o cualquiera con las herramientas de
 * desarrollo: un `tamano: 400` dejaría la pantalla inutilizable y sin forma
 * de llegar al botón para arreglarlo.
 */
export function leerPreferencias(): Preferencias {
  let crudo: unknown = null;
  try {
    const t = localStorage.getItem(CLAVE);
    crudo = t ? JSON.parse(t) : null;
  } catch {
    /* sin almacenamiento (modo privado, bloqueado) o JSON roto: valores iniciales */
  }
  const o = (crudo && typeof crudo === "object" ? crudo : {}) as Record<string, unknown>;
  const bool = (k: keyof Preferencias) => (typeof o[k] === "boolean" ? (o[k] as boolean) : (PREFERENCIAS_INICIALES[k] as boolean));
  return {
    tamano: TAMANOS.includes(o.tamano as Tamano) ? (o.tamano as Tamano) : PREFERENCIAS_INICIALES.tamano,
    contraste: bool("contraste"),
    dislexia: bool("dislexia"),
    sinMovimiento: bool("sinMovimiento"),
    subrayarEnlaces: bool("subrayarEnlaces"),
  };
}

function escribir(p: Preferencias): void {
  try {
    localStorage.setItem(CLAVE, JSON.stringify(p));
  } catch {
    /* sin almacenamiento: la preferencia vale para esta página, que es lo que importa ahora */
  }
}

function borrar(): void {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* nada que borrar si no hay almacenamiento */
  }
}

/* ---------------- Aplicar a <html> ---------------- */

/*
 * El movimiento reducido del SISTEMA también pone `sin-movimiento`. Las reglas
 * de base.css cuelgan solo de la clase (ver su comentario), así que quien lo
 * tiene pedido en Windows o en el iPad no depende de encontrar este panel.
 */
function sistemaPideMenosMovimiento(): boolean {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function aplicarPreferencias(p: Preferencias, raiz: HTMLElement = document.documentElement): void {
  // 100 % se QUITA en vez de escribirse: así manda la hoja de estilos y el
  // tamaño que la persona tenga configurado en su navegador, sin un estilo en
  // línea que lo tape.
  if (p.tamano === 100) raiz.style.removeProperty("font-size");
  else raiz.style.fontSize = `${p.tamano}%`;
  raiz.classList.toggle(CLASES.contraste, p.contraste);
  raiz.classList.toggle(CLASES.dislexia, p.dislexia);
  raiz.classList.toggle(CLASES.sinMovimiento, p.sinMovimiento || sistemaPideMenosMovimiento());
  raiz.classList.toggle(CLASES.subrayarEnlaces, p.subrayarEnlaces);
}

/* ---------------- Estado compartido (panel y pruebas) ---------------- */

let actuales: Preferencias = { ...PREFERENCIAS_INICIALES };
const oyentes = new Set<() => void>();

function avisar(): void {
  for (const f of oyentes) f();
}

/** Para `useSyncExternalStore`: el panel se entera si cambian desde otra pestaña o al cambiar de sesión. */
export function suscribirPreferencias(f: () => void): () => void {
  oyentes.add(f);
  return () => { oyentes.delete(f); };
}

export function preferenciasActuales(): Preferencias {
  return actuales;
}

function hayAlguienDentro(): boolean {
  try {
    return getSession() !== null;
  } catch {
    return false;
  }
}

/** Cambia una o varias preferencias: las aplica, las guarda y avisa al panel. */
export function cambiarPreferencias(cambio: Partial<Preferencias>): Preferencias {
  actuales = { ...actuales, ...cambio };
  aplicarPreferencias(actuales);
  escribir(actuales);
  // Elegidas en la pantalla de entrada: que sobrevivan al "Entrar" (ver abajo).
  try {
    if (!hayAlguienDentro()) sessionStorage.setItem(MARCA_ANTES_DE_ENTRAR, "1");
  } catch {
    /* sin sessionStorage: se restablecerán al entrar, que es el lado seguro */
  }
  avisar();
  return actuales;
}

/** Vuelve a los valores iniciales y olvida lo guardado. */
export function restablecerPreferencias(): void {
  actuales = { ...PREFERENCIAS_INICIALES };
  aplicarPreferencias(actuales);
  borrar();
  try {
    sessionStorage.removeItem(MARCA_ANTES_DE_ENTRAR);
  } catch {
    /* nada */
  }
  avisar();
}

/*
 * Al cambiar de sesión se restablece TODO — con UNA excepción: lo que se eligió
 * en la pantalla de entrada, ANTES de entrar. `auth` llama a este limpiador
 * también al iniciar sesión (por si alguien cerró el navegador sin salir), y sin
 * la excepción la persona que subió la letra para poder leer el formulario de
 * entrada la perdería justo al entrar, que es cuando más la necesita.
 *
 * El caso que se quiere evitar sigue cubierto: lo que un visitante elige
 * DENTRO (con la sesión del kiosco abierta) no lleva la marca, y se borra al
 * salir o cuando el kiosco se reinicia por inactividad.
 *
 * La lectura en voz alta también se corta: que siga hablando para el siguiente
 * sería tan raro como que le quede la letra grande.
 */
alCambiarDeSesion(() => {
  detenerLectura();
  let antesDeEntrar = false;
  try {
    antesDeEntrar = sessionStorage.getItem(MARCA_ANTES_DE_ENTRAR) === "1";
    sessionStorage.removeItem(MARCA_ANTES_DE_ENTRAR);
  } catch {
    /* sin sessionStorage: se restablece, que es el lado seguro */
  }
  if (!antesDeEntrar) restablecerPreferencias();
});

let iniciado = false;

/**
 * Aplica lo guardado a <html>. Se llama en main.tsx ANTES del primer render.
 * Idempotente: llamarla dos veces no duplica los oyentes.
 */
export function iniciarAccesibilidad(): void {
  actuales = leerPreferencias();
  aplicarPreferencias(actuales);
  if (iniciado) return;
  iniciado = true;
  // Si la persona cambia el movimiento reducido del sistema con la app abierta.
  try {
    window.matchMedia?.("(prefers-reduced-motion: reduce)").addEventListener?.("change", () => aplicarPreferencias(actuales));
  } catch {
    /* navegadores viejos sin addEventListener en MediaQueryList: se aplica al recargar */
  }
  // Otra pestaña del mismo navegador cambió o restableció las preferencias.
  window.addEventListener("storage", (e) => {
    if (e.key !== CLAVE && e.key !== null) return;
    actuales = leerPreferencias();
    aplicarPreferencias(actuales);
    avisar();
  });
}

/* ---------------- Leer en voz alta ---------------- */

/*
 * Mismo patrón que el reproductor de lecciones (LessonPlayer) y el de
 * Alfabetización: `speechSynthesis` del navegador, sin servicios externos, con
 * detección de soporte antes de tocar nada. Si no hay voz, el botón lo dice en
 * vez de fallar en silencio.
 */

let leyendo = false;
const oyentesLectura = new Set<() => void>();

function marcarLeyendo(v: boolean): void {
  if (leyendo === v) return;
  leyendo = v;
  for (const f of oyentesLectura) f();
}

export function suscribirLectura(f: () => void): () => void {
  oyentesLectura.add(f);
  return () => { oyentesLectura.delete(f); };
}

export function estaLeyendo(): boolean {
  return leyendo;
}

export function puedeLeerEnVozAlta(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window && typeof SpeechSynthesisUtterance !== "undefined";
}

/*
 * La voz en español que más se parezca a la de aquí: primero Puerto Rico,
 * luego la de Estados Unidos (acento latinoamericano, la que traen casi todos
 * los Windows y Android), y si no, cualquier español. `getVoices()` puede
 * venir vacío al principio en Chrome (las carga en diferido); entonces no se
 * elige voz y el navegador usa la de `lang`.
 */
function vozEnEspanol(): SpeechSynthesisVoice | null {
  let voces: SpeechSynthesisVoice[] = [];
  try {
    voces = window.speechSynthesis.getVoices() ?? [];
  } catch {
    return null;
  }
  const norm = (l: string) => l.replace("_", "-").toLowerCase();
  for (const pref of ["es-pr", "es-us", "es-mx", "es-419"]) {
    const v = voces.find((x) => norm(x.lang) === pref);
    if (v) return v;
  }
  return voces.find((x) => norm(x.lang).startsWith("es")) ?? null;
}

/*
 * En trozos de frase, no de una vez: Chrome corta en seco las locuciones largas
 * pasados unos 15 segundos, y una página entera leída de un tirón se quedaba
 * a media frase sin avisar. Además así "Detener" responde al instante.
 */
export function trozosParaLeer(texto: string, max = 220): string[] {
  const limpio = texto.replace(/\s+/g, " ").trim();
  if (!limpio) return [];
  const frases = limpio.match(/[^.!?¡¿…]+[.!?…]*|[¡¿][^.!?]*[.!?]*/g) ?? [limpio];
  const trozos: string[] = [];
  let actual = "";
  for (const f0 of frases) {
    const f = f0.trim();
    if (!f) continue;
    if ((actual + " " + f).trim().length <= max) {
      actual = (actual + " " + f).trim();
      continue;
    }
    if (actual) trozos.push(actual);
    // Una sola "frase" más larga que el máximo (una lista sin puntos): por palabras.
    if (f.length > max) {
      let resto = "";
      for (const palabra of f.split(" ")) {
        if ((resto + " " + palabra).trim().length > max && resto) {
          trozos.push(resto);
          resto = palabra;
        } else resto = (resto + " " + palabra).trim();
      }
      actual = resto;
    } else actual = f;
  }
  if (actual) trozos.push(actual);
  return trozos;
}

/**
 * Qué se lee: lo seleccionado si hay algo, y si no, el contenido principal
 * (`<main>`), sin el menú lateral. En la pantalla de entrada no hay `<main>`
 * y se lee la aplicación entera. El panel vive en un portal FUERA de `#root`,
 * así que nunca se lee a sí mismo.
 */
export function textoParaLeer(seleccion?: string): string {
  const sel = (seleccion ?? window.getSelection?.()?.toString() ?? "").trim();
  if (sel) return sel;
  const zona = document.querySelector("main") ?? document.getElementById("root");
  if (!zona) return "";
  // `innerText` respeta lo oculto con CSS (no lee menús cerrados ni `.sr-only`
  // de más); jsdom no lo implementa, de ahí el respaldo.
  return ((zona as HTMLElement).innerText ?? zona.textContent ?? "").trim();
}

export function leerEnVozAlta(texto: string): boolean {
  if (!puedeLeerEnVozAlta()) return false;
  const trozos = trozosParaLeer(texto);
  if (trozos.length === 0) return false;
  const synth = window.speechSynthesis;
  synth.cancel();
  const voz = vozEnEspanol();
  trozos.forEach((t, i) => {
    const u = new SpeechSynthesisUtterance(t);
    u.lang = voz?.lang ?? "es-PR";
    if (voz) u.voice = voz;
    // Un poco más despacio que el normal: lo escuchan niños de primero y
    // adultos que están aprendiendo a leer (el mismo criterio que LessonPlayer).
    u.rate = 0.95;
    if (i === trozos.length - 1) u.onend = () => marcarLeyendo(false);
    // `cancel()` dispara error "interrupted" en Chrome: también es fin.
    u.onerror = () => marcarLeyendo(false);
    synth.speak(u);
  });
  marcarLeyendo(true);
  return true;
}

export function detenerLectura(): void {
  if (puedeLeerEnVozAlta()) {
    try {
      window.speechSynthesis.cancel();
    } catch {
      /* nada que parar */
    }
  }
  marcarLeyendo(false);
}
